/**
 * <custom-blog-filter>
 *
 * Progressive enhancement for the blog category pills and pagination:
 * every link renders as a real <a href>, so the section works with
 * JavaScript disabled. When JS is available:
 *
 *  - As soon as the section loads, every filter pill's page is
 *    pre-fetched in the background (via Shopify's Section Rendering
 *    API, `?sections=...`, so only this section's markup is
 *    transferred, not the whole page) and cached by URL.
 *  - Clicking a pill or pagination link swaps in the (usually already
 *    cached) content with a fade transition and updates the browser
 *    history, instead of causing a full page reload.
 */
class CustomBlogFilter extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.results = this.querySelector('[data-custom-blog-filter-results]');
    this.nav = this.querySelector('[data-custom-blog-filter-nav]');
    this.cache = new Map();
    this.requestCounter = 0;

    this.addEventListener('click', this.onClick.bind(this));
    window.addEventListener('popstate', this.onPopState.bind(this));

    this.prefetchFilterPills();
  }

  prefetchFilterPills() {
    if (!this.nav) return;

    this.nav.querySelectorAll('[data-custom-blog-filter-link]').forEach((link) => {
      this.fetchSection(link.href).catch(() => {
        // Prefetch failures are silent; a real click will retry and fall
        // back to a normal page navigation if it fails again.
      });
    });
  }

  onClick(event) {
    const link = event.target.closest('[data-custom-blog-filter-link]');
    const isPlainClick = !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);

    if (!link || !isPlainClick) return;

    event.preventDefault();
    this.goTo(link.href, { pushState: true });
  }

  onPopState() {
    this.goTo(window.location.href, { pushState: false });
  }

  sectionRenderingUrl(url) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}sections=${encodeURIComponent(this.sectionId)}`;
  }

  fetchSection(url) {
    if (this.cache.has(url)) return this.cache.get(url);

    const request = fetch(this.sectionRenderingUrl(url))
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed with ${response.status}`);
        return response.json();
      })
      .then((payload) => payload[this.sectionId]);

    this.cache.set(url, request);
    return request;
  }

  async goTo(url, { pushState }) {
    if (!this.results || !this.sectionId) return;

    const requestId = ++this.requestCounter;

    try {
      const html = await this.fetchSection(url);

      // A newer click already took over while this one was in flight.
      if (requestId !== this.requestCounter) return;

      const nextDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextResults = nextDocument.querySelector('[data-custom-blog-filter-results]');
      const nextNav = nextDocument.querySelector('[data-custom-blog-filter-nav]');

      await this.swapContent(() => {
        if (nextResults) this.results.innerHTML = nextResults.innerHTML;
        if (nextNav && this.nav) this.nav.innerHTML = nextNav.innerHTML;
      });

      if (pushState) window.history.pushState({}, '', url);
      this.results.focus();
    } catch (error) {
      this.cache.delete(url);
      window.location.assign(url);
    }
  }

  swapContent(applyChanges) {
    const FADE_DURATION = 220;

    return new Promise((resolve) => {
      this.results.classList.add('custom-blog-filter__results--transitioning');

      window.setTimeout(() => {
        applyChanges();
        // Force a reflow so the fade-in transition replays from scratch.
        this.results.offsetHeight;
        this.results.classList.remove('custom-blog-filter__results--transitioning');
        resolve();
      }, FADE_DURATION);
    });
  }
}

customElements.define('custom-blog-filter', CustomBlogFilter);
